import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field, getIn } from 'formik';
import { object, string } from 'yup';
import { Box, Flex, Text } from 'rebass';
import { useHistory, useParams } from 'react-router-dom';
import Loading from 'app/loading';
import Modal from 'app/modal/v3';
import Button from 'app/buttons';
import { PrimaryButton } from './components/FtButton';
import PhotoField from './components/PhotoField';
import VideoField from './components/VideoField';
import TextField from 'app/form/formik/text';
import SelectField from 'app/form/formik/select';
import DateField from 'app/form/formik/date';
import { uploadMedium } from 'story-media/upload-medium';
import { patch } from 'utils/api';

import { RELATIONSHIP_OPTIONS } from './constants';
import {
  useFamilyTreeSWR,
  postFamilyMember,
  patchFamilyMember,
  uploadFamilyMemberAvatar,
  uploadFamilyMemberVideo,
  createdMemberId,
  createdRelationshipId,
} from './api';
import { parseFamilyTree, memberFullName } from './helpers';
import { ErrorMessageWrap } from 'app/form/formik/text/styled';

const fieldLabel = (text, required) => (
  <Text
    sx={{
      color: 'codGrey',
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '-0.084px',
    }}
  >
    {text}
    {required && <span style={{ color: 'red' }}> *</span>}
  </Text>
);

// Persist the avatar. We upload ONLY the original file the user chose and store the
// crop + zoom params on the media object (no separate canvas image).
//
// `avatar` shapes:
//   - { file, zoom, 'crop-left', 'crop-top' }  → from the cropper (file may be null
//       when only the crop/zoom changed on an existing image)
//   - Blob                                     → dropped but not cropped
//   - existing image attrs (no `file` key) / '' → untouched → no-op
const persistAvatar = async (memberId, avatar, existingImageId, existingLockVersion, uploadFailures = []) => {
  const isBlob = avatar instanceof Blob;

  const isEdit = !isBlob && avatar && typeof avatar === 'object' && 'file' in avatar;
  if (!isBlob && !isEdit) return; // photo untouched

  const file = isBlob ? avatar : avatar.file; // original file, or null for crop-only edit
  const cropParams = isBlob
    ? null
    : {
      zoom: avatar.zoom,
      'crop-left': avatar['crop-left'],
      'crop-top': avatar['crop-top'],
      'left': avatar?.left,
      'top': avatar?.top,
      'crop-width': `${avatar?.['crop-width']}`,
      'crop-height': `${avatar?.['crop-height']}`,
    };

  let mediaId = existingImageId;
  let lockVersion = existingLockVersion;

  // Upload the original image (new / replacement).
  if (file instanceof Blob) {
    const result = await uploadFamilyMemberAvatar(memberId, file.name || 'avatar.jpg');
    const medium = result?.data?.entities?.images?.[result?.data?.result?.images?.[0]];
    if (!medium) return;
    mediaId = medium.id;
    lockVersion = medium['lock-version'];
    if (medium['upload-url']) {
      // Previously `await uploadMediumFile(...)`, which threw. The member had
      // already been created by this point, so a failed photo aborted persistVideo,
      // the revalidate and the redirect -- leaving a saved member with no video, on
      // a form claiming the whole save had failed. Report it instead of throwing.
      const upload = await uploadMedium({ medium, mediumType: 'Image', file });
      if (!upload.ok) uploadFailures.push('photo');
    }
  }

  // Store crop + zoom on the media.
  if (!mediaId || !cropParams) return;
  const attributes = { ...cropParams };
  if (lockVersion != null) attributes['lock-version'] = lockVersion;
  await patch(`/v2/media/${mediaId}`, { data: { attributes } });
};

// Persist the member's video (if any), linked to the member — same flow as the avatar.
const persistVideo = async (memberId, video, relationShipKind, uploadFailures = []) => {
  if (!(video instanceof Blob)) return;
  const result = await uploadFamilyMemberVideo(memberId, video.name || 'video.webm', {}, relationShipKind);
  const medium = result?.data?.entities?.videos?.[result?.data?.result?.videos?.[0]];
  if (medium?.['upload-url']) {
    const upload = await uploadMedium({ medium, mediumType: 'Video', file: video });
    if (!upload.ok) uploadFailures.push('video');
  }
};

// Add / Edit a family member. Mounted as an overlay route from family-tree/index.js.
const MemberFormModal = ({ journalId, babyName, baseRoute }) => {
  const { replace, push } = useHistory();
  const { memberId } = useParams();
  const isEdit = !!memberId;
  const [parentHeight, setParentHeight] = useState(null);

  const { data, revalidate } = useFamilyTreeSWR(journalId);
  const { members, groups } = parseFamilyTree(data);
  const existing = isEdit ? members.find((m) => m.id === memberId) : null;
  const parentLimitReached = groups.find((group) => group.key === 'parents')?.members?.length >= 4

  const [submitError, setSubmitError] = useState(null);
  const close = () => replace(baseRoute);
  const relationshipOriginal = existing?.['relationshipKind'] || existing?.['relationship'] ? String(existing['relationshipKind'] || existing?.['relationship']).toLowerCase() : '';

  const initialValues = {
    'first-name': existing?.firstName || '',
    'last-name': existing?.lastName || '',
    label: existing?.['personal-name'] || '',
    relationship: relationshipOriginal || '',
    birthdate: existing?.['date-of-birth'] || '',
    avatar: existing?.image || '',
    video: existing?.video || '',
  };

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitError(null);
    const { avatar, video, ...rest } = values;

    const attributes = {
      'first-name': rest['first-name'],
      'last-name': rest['last-name'] || undefined,
      'personal-name': rest.label || undefined,
      'relationship-kind': rest.relationship,
      'date-of-birth': rest.birthdate || undefined,
    };

    try {
      // Media (avatar/video) is owned by the underlying Member, not the family_member
      // link — use the relative's member id (from GET /family_tree on edit, or the
      // POST response on create).
      let mediaOwnerId = existing?.memberId;
      let relationshipId = existing?.raw?.['label-id'];

      if (isEdit) {
        await patchFamilyMember(journalId, memberId, attributes);
      } else {
        const response = await postFamilyMember(journalId, attributes);

        relationshipId = createdRelationshipId(response) || relationshipId;
        mediaOwnerId = createdMemberId(response) || mediaOwnerId;
      }
      // Collected rather than thrown, so one failed upload no longer cancels the
      // other one or the navigation. The profile itself is already saved by here.
      const uploadFailures = [];
      if (mediaOwnerId) {
        await persistAvatar(mediaOwnerId, avatar, existing?.imageId, existing?.image?.['lock-version'], uploadFailures);
        await persistVideo(relationshipId, video, existing?.relationshipKind, uploadFailures);
      }
      revalidate();

      if (uploadFailures.length) {
        setSubmitError(
          `This profile was saved, but the ${uploadFailures.join(' and ')} could not be uploaded. ` +
          'You can edit the profile to try again.'
        );
        return;
      }

      replace(`${baseRoute}?added=1`);
    } catch (e) {
      setSubmitError('Something went wrong saving this profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = object().shape({
    'first-name': string().trim().required('First name is required'),
    relationship: string().required('Relationship is required'),
    birthdate: string().required('Birthdate is required'),
  });

  const BackIcon = <svg style={{ minWidth: '20px' }} xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.70711 0.292893C6.09763 0.683417 6.09763 1.31658 5.70711 1.70711L2.41421 5L5.70711 8.29289C6.09763 8.68342 6.09763 9.31658 5.70711 9.70711C5.31658 10.0976 4.68342 10.0976 4.29289 9.70711L0.292894 5.70711C-0.0976306 5.31658 -0.0976306 4.68342 0.292894 4.29289L4.29289 0.292893C4.68342 -0.0976311 5.31658 -0.0976311 5.70711 0.292893Z" fill="#0E0E0E" />
  </svg>

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {(formik) => {
        const parentCantBeAdded = parentLimitReached && (formik?.values?.relationship === 'mother' || formik?.values?.relationship === 'father')

        return (
          <Modal
            title={isEdit ? 'Edit Family Member' : 'Add a Family Member'}
            close={{ onClose: close }}
            closeOnOutsideClick={false}
            shadeProps={{ pt: [0, 0, 4, 4] }}
            footer={
              <Flex justifyContent="space-between" alignItems="center" width="100%" py={2} pr={1}
                sx={{
                  background: 'white',
                  position: ['fixed', 'absolute', 'absolute'],
                  bottom: [0, 1, 2],
                  left: 0,
                }}
              >
                <Button type="button" style={{ color: '#0E0E0E', background: 'transparent', border: 0, boxShadow: 'unset' }} onClick={close}>
                  {BackIcon} Back
                </Button>
                <PrimaryButton
                  loading={formik.isSubmitting}
                  disabled={!formik.isValid || formik.isSubmitting || parentCantBeAdded}
                  onClick={formik.submitForm}
                >
                  Save
                </PrimaryButton>
              </Flex>
            }
          >
            <Form autoComplete="off">
              {formik.isSubmitting ?
                <Flex flex="1 1 auto" height={parentHeight} alignItems="center" justifyContent="center" >
                  <Loading local />
                </Flex>
                : <Box px={[3, 4]} py={2} mb={9} css="display: grid; grid-gap: 14px;" ref={(el) => {
                  if (!el) return;
                  setParentHeight(el.getBoundingClientRect().height);
                }}>
                  <Field
                    component={TextField}
                    name="first-name"
                    id="first-name"
                    label={fieldLabel('First Name', true)}
                    placeholder="First name"
                    showError
                  />
                  <Field
                    component={TextField}
                    name="label"
                    id="label"
                    label={fieldLabel(`What will ${babyName} call them?`)}
                    placeholder="ex. Auntie Jo"
                    showError
                  />
                  <Field
                    component={SelectField}
                    name="relationship"
                    id="relationship"
                    label={fieldLabel(`Relationship to ${babyName}`, true)}
                    options={RELATIONSHIP_OPTIONS}
                    placeholder="Select a relationship"
                    showError
                  />
                  {(relationshipOriginal !== 'mother' && relationshipOriginal !== 'father') && parentCantBeAdded && <ErrorMessageWrap sx={{ color: 'red', fontFamily: 'Poppins', fontSize: '13px' }}>Maximum parent limit reached. Please select a different relationship.</ErrorMessageWrap>}

                  <Field
                    component={DateField}
                    name="birthdate"
                    id="birthdate"
                    label={fieldLabel('Birthdate', true)}
                    showError
                  />

                  {/* Profile Photo — Upload row → Edit Photo modal (circular cropper) */}
                  <Field
                    name="avatar"
                    id="avatar"
                    component={PhotoField}
                    label="Profile Photo"
                    circular
                    currentCover={existing?.imageId || ''}
                    currentImage={formik.values['avatar-canvas-image'] || existing?.image || ''}
                    onRemoved={revalidate}
                  />

                  {/* Video — Upload / Record rows; uploaded & linked to the member on Save */}
                  <Field
                    name="video"
                    component={VideoField}
                    label="Video"
                    currentVideoUrl={existing?.video?.['preview-url'] || existing?.raw?.['video_url'] || ''}
                  />

                  {submitError && (
                    <Text color="red" fontFamily="Poppins" fontSize="13px">
                      {submitError}
                    </Text>
                  )}

                  {isEdit && (
                    <Text
                      as="button"
                      type="button"
                      onClick={() => push(`${baseRoute}/members/${memberId}/delete`)}
                      sx={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'red',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        textAlign: 'center',
                      }}
                    >
                      {`Delete ${memberFullName(existing) || 'this'} profile`}
                    </Text>
                  )}
                </Box>}
            </Form>
          </Modal>
        )
      }}
    </Formik>
  );
};

MemberFormModal.propTypes = {
  journalId: PropTypes.string.isRequired,
  babyName: PropTypes.string,
  baseRoute: PropTypes.string.isRequired,
};

MemberFormModal.defaultProps = {
  babyName: 'your baby',
};

export default MemberFormModal;
